import React, { useCallback, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import ImageExt from "@tiptap/extension-image";
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Strikethrough,
    Code,
    Heading1,
    Heading2,
    Heading3,
    List,
    ListOrdered,
    ListChecks,
    Quote,
    Minus,
    Link as LinkIcon,
    Image as ImageIcon,
    Table as TableIcon,
    Highlighter,
    Undo,
    Redo,
    Save,
} from "lucide-react";

export default function WikiEditor({ page, onSave }) {
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { class: "wiki-link" },
            }),
            Placeholder.configure({
                placeholder: "Start writing…",
            }),
            Underline,
            Highlight,
            TaskList,
            TaskItem.configure({ nested: true }),
            Table.configure({ resizable: false }),
            TableRow,
            TableCell,
            TableHeader,
            ImageExt,
        ],
        content: page.body ? tryParseJSON(page.body) : "",
        onUpdate: () => {
            if (!dirty) setDirty(true);
        },
    });

    const handleSave = useCallback(async () => {
        if (!editor || saving) return;
        setSaving(true);
        try {
            const json = JSON.stringify(editor.getJSON());
            const plain = editor.getText();
            await onSave(json, plain);
            setDirty(false);
        } finally {
            setSaving(false);
        }
    }, [editor, onSave, saving]);

    // Keyboard shortcut: Cmd+S
    const handleKeyDown = useCallback(
        (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "s") {
                e.preventDefault();
                handleSave();
            }
        },
        [handleSave],
    );

    React.useEffect(() => {
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    if (!editor) return null;

    return (
        <div>
            {/* Toolbar */}
            <div className="border-b-3 border-black bg-gray-50 sticky top-[60px] z-10 flex items-center gap-0.5 flex-wrap px-2 py-1.5">
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive("bold")}
                    title="Bold"
                >
                    <Bold size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive("italic")}
                    title="Italic"
                >
                    <Italic size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    active={editor.isActive("underline")}
                    title="Underline"
                >
                    <UnderlineIcon size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleStrike().run()
                    }
                    active={editor.isActive("strike")}
                    title="Strikethrough"
                >
                    <Strikethrough size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleHighlight().run()
                    }
                    active={editor.isActive("highlight")}
                    title="Highlight"
                >
                    <Highlighter size={14} strokeWidth={3} />
                </ToolBtn>

                <Divider />

                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 1 }).run()
                    }
                    active={editor.isActive("heading", { level: 1 })}
                    title="Heading 1"
                >
                    <Heading1 size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 2 }).run()
                    }
                    active={editor.isActive("heading", { level: 2 })}
                    title="Heading 2"
                >
                    <Heading2 size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleHeading({ level: 3 }).run()
                    }
                    active={editor.isActive("heading", { level: 3 })}
                    title="Heading 3"
                >
                    <Heading3 size={14} strokeWidth={3} />
                </ToolBtn>

                <Divider />

                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleBulletList().run()
                    }
                    active={editor.isActive("bulletList")}
                    title="Bullet List"
                >
                    <List size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleOrderedList().run()
                    }
                    active={editor.isActive("orderedList")}
                    title="Numbered List"
                >
                    <ListOrdered size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleTaskList().run()
                    }
                    active={editor.isActive("taskList")}
                    title="Checklist"
                >
                    <ListChecks size={14} strokeWidth={3} />
                </ToolBtn>

                <Divider />

                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleBlockquote().run()
                    }
                    active={editor.isActive("blockquote")}
                    title="Quote"
                >
                    <Quote size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().toggleCodeBlock().run()
                    }
                    active={editor.isActive("codeBlock")}
                    title="Code Block"
                >
                    <Code size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor.chain().focus().setHorizontalRule().run()
                    }
                    title="Divider"
                >
                    <Minus size={14} strokeWidth={3} />
                </ToolBtn>

                <Divider />

                <ToolBtn
                    onClick={() => {
                        const url = window.prompt("Enter URL:");
                        if (url)
                            editor
                                .chain()
                                .focus()
                                .setLink({ href: url })
                                .run();
                    }}
                    active={editor.isActive("link")}
                    title="Link"
                >
                    <LinkIcon size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => {
                        const url = window.prompt("Image URL:");
                        if (url)
                            editor.chain().focus().setImage({ src: url }).run();
                    }}
                    title="Image"
                >
                    <ImageIcon size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() =>
                        editor
                            .chain()
                            .focus()
                            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                            .run()
                    }
                    title="Insert Table"
                >
                    <TableIcon size={14} strokeWidth={3} />
                </ToolBtn>

                <Divider />

                <ToolBtn
                    onClick={() => editor.chain().focus().undo().run()}
                    title="Undo"
                >
                    <Undo size={14} strokeWidth={3} />
                </ToolBtn>
                <ToolBtn
                    onClick={() => editor.chain().focus().redo().run()}
                    title="Redo"
                >
                    <Redo size={14} strokeWidth={3} />
                </ToolBtn>

                {/* Save */}
                <div className="ml-auto">
                    <button
                        onClick={handleSave}
                        disabled={saving || !dirty}
                        className={`font-display text-xs px-3 py-1.5 brut-border brut-press flex items-center gap-1.5
                            ${
                                dirty
                                    ? "bg-[#E8241A] text-white"
                                    : "bg-gray-200 text-gray-400"
                            }`}
                    >
                        <Save size={12} strokeWidth={3} />
                        {saving ? "SAVING…" : dirty ? "SAVE (⌘S)" : "SAVED"}
                    </button>
                </div>
            </div>

            {/* Editor area */}
            <div className="wiki-editor prose prose-neutral max-w-none py-6">
                <EditorContent editor={editor} />
            </div>
        </div>
    );
}

function ToolBtn({ children, onClick, active, title }) {
    return (
        <button
            onClick={onClick}
            title={title}
            className={`p-1.5 ${
                active
                    ? "bg-black text-white"
                    : "bg-white hover:bg-gray-100"
            } brut-border brut-press`}
        >
            {children}
        </button>
    );
}

function Divider() {
    return <span className="w-[3px] h-5 bg-black mx-1 opacity-20" />;
}

function tryParseJSON(str) {
    try {
        return JSON.parse(str);
    } catch {
        return str || "";
    }
}
